import axios from "axios";
import { toast } from "sonner";

import type { ComplianceDownload, ComplianceTaskStatus } from "@/lib/api/compliance";
import { downloadBlob, readBlobErrorDetail } from "@/lib/download";

/**
 * The consultant's page: Policies (backend#894) became Compliance, and
 * Compliance became My Company (#1132). Both old prefixes redirect, keeping
 * the sub-path, so task and agreement links in old emails still open.
 */
export const MY_COMPANY_PATH = "/my-company";
export const LEGACY_COMPLIANCE_PATH = "/compliance";
export const ADMIN_COMPLIANCE_PATH = "/admin/compliance";

/** The My Company tabs; `?tab=` outside this list opens the default. */
export const MY_COMPANY_TABS = ["agreements", "documents", "policies", "team"] as const;
export type MyCompanyTab = (typeof MY_COMPANY_TABS)[number];
export const DEFAULT_MY_COMPANY_TAB: MyCompanyTab = "agreements";

/** "/my-company?tab=documents": where a detail page's back link lands. */
export const myCompanyTabPath = (tab: MyCompanyTab): string => `${MY_COMPANY_PATH}?tab=${tab}`;

/**
 * Compliance upload rules (backend#895), shared by the consultant task page
 * and the admin views. The server is authoritative: it checks the file's
 * content, not its name. These checks only give a quick message first.
 */
export const MAX_COMPLIANCE_UPLOAD_BYTES = 10 * 1024 * 1024;

const TYPE_INFO: Record<string, { label: string; extensions: string[] }> = {
  "application/pdf": { label: "PDF", extensions: [".pdf"] },
  "image/png": { label: "PNG", extensions: [".png"] },
  "image/jpeg": { label: "JPG", extensions: [".jpg", ".jpeg"] },
};

/** Requested, submitted or rejected: the task still needs something from someone. */
export const isOpenTask = (status: ComplianceTaskStatus): boolean =>
  status === "requested" || status === "submitted" || status === "rejected";

/** The file input's `accept` value: MIME types plus extensions, for pickers that key on either. */
export const acceptAttribute = (acceptedTypes: string[]): string =>
  acceptedTypes.flatMap(type => [type, ...(TYPE_INFO[type]?.extensions ?? [])]).join(",");

/** "PDF", "PDF or PNG", "PDF, PNG or JPG". */
export const describeAcceptedTypes = (acceptedTypes: string[]): string => {
  const labels = acceptedTypes.map(type => TYPE_INFO[type]?.label ?? type);
  if (labels.length <= 1) {
    return labels.join("");
  }
  return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
};

const matchesType = (file: File, acceptedTypes: string[]): boolean => {
  if (file.type) {
    return acceptedTypes.includes(file.type);
  }
  // Some pickers report no MIME type; fall back to the extension.
  const name = file.name.toLowerCase();
  return acceptedTypes.some(type =>
    (TYPE_INFO[type]?.extensions ?? []).some(ext => name.endsWith(ext))
  );
};

/** Null when the file looks acceptable, otherwise a short reason to show. */
export const validateComplianceFile = (file: File, acceptedTypes: string[]): string | null => {
  if (!matchesType(file, acceptedTypes)) {
    return `Upload a ${describeAcceptedTypes(acceptedTypes)} file.`;
  }
  if (file.size > MAX_COMPLIANCE_UPLOAD_BYTES) {
    return "The file is larger than 10 MB.";
  }
  return null;
};

/** 1536 → "1.5 KB". */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Fetch a stored compliance file and save it. The download suppresses the
 * interceptor toast, so exactly one message surfaces here: the backend's own
 * detail where there is one, and a plain wording for a deleted file (410).
 */
export const downloadComplianceFile = async (
  load: () => Promise<ComplianceDownload>
): Promise<void> => {
  try {
    const { blob, filename } = await load();
    downloadBlob(blob, filename);
  } catch (error) {
    const detail = await readBlobErrorDetail(error);
    if (axios.isAxiosError(error) && error.response?.status === 410) {
      toast.error("This file has been deleted.");
      return;
    }
    toast.error(detail ?? "Could not download the file. Please try again.");
  }
};
